import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

function cloneDefault(value) {
  if (Array.isArray(value)) return [...value]
  return value
}

function parseField(raw, config) {
  const fallback = config.default
  if (raw == null || raw === '') return cloneDefault(fallback)

  switch (config.type) {
    case 'string[]': {
      return String(raw)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    }
    case 'boolean':
      return raw === '1' || raw === 'true'
    case 'number': {
      const n = Number(raw)
      return Number.isFinite(n) ? n : cloneDefault(fallback)
    }
    case 'string':
    default:
      return String(raw)
  }
}

function serializeField(value, config) {
  const def = config.default
  switch (config.type) {
    case 'string[]': {
      const arr = Array.isArray(value) ? value.filter(Boolean) : []
      const defArr = Array.isArray(def) ? def : []
      if (arr.length === 0 && defArr.length === 0) return null
      if (arr.length === defArr.length && arr.every((v, i) => v === defArr[i])) return null
      return arr.join(',')
    }
    case 'boolean': {
      const bool = Boolean(value)
      if (bool === Boolean(def)) return null
      return bool ? '1' : '0'
    }
    case 'number': {
      const n = Number(value)
      if (!Number.isFinite(n) || n === Number(def)) return null
      return String(n)
    }
    case 'string':
    default: {
      const s = value == null ? '' : String(value)
      if (s === String(def ?? '')) return null
      return s
    }
  }
}

function valuesEqual(a, b, type) {
  if (type === 'string[]') {
    const aa = Array.isArray(a) ? a : []
    const bb = Array.isArray(b) ? b : []
    if (aa.length !== bb.length) return false
    return aa.every((v, i) => v === bb[i])
  }
  return a === b
}

function parseAllFromParams(searchParams, fields) {
  const next = {}
  for (const [key, config] of Object.entries(fields)) {
    next[key] = parseField(searchParams.get(key), config)
  }
  return next
}

/**
 * Sync list/filter UI state with URL search params (HashRouter-friendly).
 *
 * @param {{
 *   fields: Record<string, { type: 'string'|'string[]'|'boolean'|'number', default: any, debounce?: boolean }>,
 *   preserveKeys?: string[],
 *   debounceMs?: number,
 *   resetPageKey?: string | null,
 * }} options
 */
export function useListUiState({
  fields,
  preserveKeys = [],
  debounceMs = 300,
  resetPageKey = null,
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const fieldsRef = useRef(fields)
  fieldsRef.current = fields
  const preserveKeysRef = useRef(preserveKeys)
  preserveKeysRef.current = preserveKeys

  const urlValues = useMemo(
    () => parseAllFromParams(searchParams, fields),
    [searchParams, fields]
  )

  const urlValuesRef = useRef(urlValues)
  urlValuesRef.current = urlValues

  const debouncedKeys = useMemo(
    () => Object.keys(fields).filter((k) => fields[k].debounce),
    [fields]
  )

  const [drafts, setDrafts] = useState(() => {
    const d = {}
    for (const key of Object.keys(fields)) {
      if (fields[key].debounce) d[key] = urlValues[key]
    }
    return d
  })

  const draftsRef = useRef(drafts)
  draftsRef.current = drafts

  const urlSyncKey = debouncedKeys.map((k) => String(urlValues[k] ?? '')).join('\0')
  useEffect(() => {
    setDrafts((prev) => {
      let changed = false
      const next = { ...prev }
      for (const key of debouncedKeys) {
        if (prev[key] !== urlValues[key]) {
          next[key] = urlValues[key]
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [urlSyncKey, debouncedKeys, urlValues])

  const writeParams = useCallback(
    (nextValues) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams()
          for (const key of preserveKeysRef.current) {
            const v = prev.get(key)
            if (v != null && v !== '') params.set(key, v)
          }
          for (const [key, config] of Object.entries(fieldsRef.current)) {
            const serialized = serializeField(nextValues[key], config)
            if (serialized != null && serialized !== '') params.set(key, serialized)
          }
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const mergeCurrent = useCallback(() => {
    const current = { ...urlValuesRef.current }
    for (const key of debouncedKeys) {
      if (draftsRef.current[key] !== undefined) current[key] = draftsRef.current[key]
    }
    return current
  }, [debouncedKeys])

  const setFields = useCallback(
    (partialOrUpdater) => {
      const current = mergeCurrent()
      const partial =
        typeof partialOrUpdater === 'function' ? partialOrUpdater(current) : partialOrUpdater
      const next = { ...current, ...partial }

      if (
        resetPageKey &&
        Object.prototype.hasOwnProperty.call(fieldsRef.current, resetPageKey) &&
        !Object.prototype.hasOwnProperty.call(partial, resetPageKey)
      ) {
        const structuralChange = Object.keys(partial).some((k) => k !== resetPageKey)
        if (structuralChange) {
          next[resetPageKey] = cloneDefault(fieldsRef.current[resetPageKey].default)
        }
      }

      for (const key of debouncedKeys) {
        if (Object.prototype.hasOwnProperty.call(partial, key)) {
          setDrafts((d) => ({ ...d, [key]: partial[key] }))
        }
      }

      writeParams(next)
    },
    [debouncedKeys, mergeCurrent, resetPageKey, writeParams]
  )

  const setField = useCallback(
    (key, valueOrUpdater) => {
      setFields((current) => {
        const value =
          typeof valueOrUpdater === 'function' ? valueOrUpdater(current[key]) : valueOrUpdater
        return { [key]: value }
      })
    },
    [setFields]
  )

  useEffect(() => {
    if (debouncedKeys.length === 0) return undefined
    const timer = setTimeout(() => {
      let needsWrite = false
      const next = { ...urlValuesRef.current }
      for (const key of debouncedKeys) {
        const draftVal = draftsRef.current[key]
        if (!valuesEqual(draftVal, urlValuesRef.current[key], fieldsRef.current[key].type)) {
          next[key] = draftVal
          needsWrite = true
        }
      }
      if (!needsWrite) return
      if (
        resetPageKey &&
        Object.prototype.hasOwnProperty.call(fieldsRef.current, resetPageKey)
      ) {
        next[resetPageKey] = cloneDefault(fieldsRef.current[resetPageKey].default)
      }
      writeParams(next)
    }, debounceMs)
    return () => clearTimeout(timer)
  }, [drafts, debouncedKeys, debounceMs, resetPageKey, writeParams])

  const setDraft = useCallback((key, value) => {
    setDrafts((prev) => ({ ...prev, [key]: value }))
  }, [])

  const reset = useCallback(() => {
    const next = {}
    for (const [key, config] of Object.entries(fieldsRef.current)) {
      next[key] = cloneDefault(config.default)
    }
    const draftNext = {}
    for (const key of debouncedKeys) {
      draftNext[key] = next[key]
    }
    setDrafts(draftNext)
    writeParams(next)
  }, [debouncedKeys, writeParams])

  const hasActive = useMemo(() => {
    for (const [key, config] of Object.entries(fields)) {
      if (key === resetPageKey) continue
      const current = debouncedKeys.includes(key) ? drafts[key] : urlValues[key]
      if (!valuesEqual(current, config.default, config.type)) return true
    }
    return false
  }, [fields, urlValues, drafts, debouncedKeys, resetPageKey])

  const inputValues = useMemo(() => {
    const merged = { ...urlValues }
    for (const key of debouncedKeys) {
      merged[key] = drafts[key]
    }
    return merged
  }, [urlValues, drafts, debouncedKeys])

  return {
    values: urlValues,
    inputValues,
    drafts,
    setField,
    setFields,
    setDraft,
    reset,
    hasActive,
  }
}
